/**
 * sidebarStore — UX state for the Conversation History Sidebar (Hybrid).
 *
 * Owns local-only chrome state that survives reloads:
 *   - `isExpanded`           → false (mini-rail 48px) vs true (panel 260px)
 *   - `collapsedProjects`    → per-project tree fold state (key = project id
 *                              or "_unassigned")
 *   - `pinnedConversations`  → conversation ids the user pinned to the
 *                              always-top "Fissate" group
 *
 * Conversation list, mutations, project metadata live elsewhere:
 *   - `useConversationsQuery` / `useConversationMutations` (chat store)
 *   - `useProjects` (projects feature)
 *
 * Persistence: `zustand/middleware/persist` against the shared
 * `zustandStorage` (localStorage on web; NOT the OS keychain — secrets
 * are NOT stored here so the web adapter is safe). Versioned key
 * (`ando.chat-sidebar.v1`) so future schema changes can migrate cleanly.
 */

import { zustandStorage } from "@/lib/storage";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface SidebarState {
  /** True = expanded panel 260px, false = collapsed mini-rail 48px. */
  isExpanded: boolean;
  /** Per-project collapse state. Key: project id, or "_unassigned". */
  collapsedProjects: Record<string, boolean>;
  /** Conversation ids that should render in the always-top "Fissate" group. */
  pinnedConversations: Record<string, true>;
  /**
   * Message ids the user pinned via the per-message toolbar (Pin icon).
   * Client-only — no BE counterpart yet — surfaces as a filled-pin
   * affordance on the bubble. The conversation_id is implicit via the
   * chat store; we key on message_id so the set is flat + survives
   * cross-conversation navigation.
   */
  pinnedMessages: Record<string, true>;

  toggleExpanded: () => void;
  setExpanded: (v: boolean) => void;
  toggleProjectCollapsed: (projectKey: string) => void;
  togglePinned: (conversationId: string) => void;
  toggleMessagePinned: (messageId: string) => void;
}

export const useSidebarStore = create<SidebarState>()(
  persist(
    (set) => ({
      // Collapsed by default per Opzione C spec — chat 38% pane keeps focus
      // on messages; the rail is one click away.
      isExpanded: false,
      collapsedProjects: {},
      pinnedConversations: {},
      pinnedMessages: {},

      toggleExpanded: () => set((s) => ({ isExpanded: !s.isExpanded })),
      setExpanded: (v) => set({ isExpanded: v }),

      toggleProjectCollapsed: (projectKey) =>
        set((s) => ({
          collapsedProjects: {
            ...s.collapsedProjects,
            [projectKey]: !s.collapsedProjects[projectKey],
          },
        })),

      togglePinned: (id) =>
        set((s) => {
          const next = { ...s.pinnedConversations };
          if (next[id]) {
            delete next[id];
          } else {
            next[id] = true;
          }
          return { pinnedConversations: next };
        }),

      toggleMessagePinned: (messageId) =>
        set((s) => {
          const next = { ...s.pinnedMessages };
          if (next[messageId]) {
            delete next[messageId];
          } else {
            next[messageId] = true;
          }
          return { pinnedMessages: next };
        }),
    }),
    {
      name: "ando.chat-sidebar.v1",
      version: 1,
      storage: createJSONStorage(() => zustandStorage),
    },
  ),
);

// Convenience selectors — keep render-time subscriptions narrow so unrelated
// state changes (e.g. pinning one chat) don't re-render the mini-rail.
export const selectSidebarExpanded = (s: SidebarState): boolean => s.isExpanded;
export const selectCollapsedProjects = (s: SidebarState): Record<string, boolean> =>
  s.collapsedProjects;
export const selectPinnedConversations = (s: SidebarState): Record<string, true> =>
  s.pinnedConversations;
export const selectPinnedMessages = (s: SidebarState): Record<string, true> =>
  s.pinnedMessages;
